/*
 * 戒导航 · 提及模块（jiedh/mention）—— 提及语法（四种形态）的词法与序列化工具
 * =============================================================================
 * 设计依据：packages/mention/extend.php 头部注释「三、做法 / 四、红线」
 *
 * 【本文件要认的四种（细分五种）提及源码】
 *   ① 帖子提及（safe）      @"显示名"#p帖子ID      例：@"admin"#p34
 *   ② 用户提及（safe）      @"显示名"#用户ID       例：@"admin"#12
 *   ③ 用户提及（旧式）      @用户名                例：@admin
 *   ④ 群组提及（safe）      @"组名"#g群组ID        例：@"Moderators"#g3
 *   ⑤ 标签提及              #标签slug              例：#announcements
 *   其中 ② 与 ③ 是同一语法位（用户提及）的两种历史写法，由后台设置
 *   flarum-mentions.allow_username_format 决定前端产出哪一种。
 *
 * 【权威口径】
 *   ① 服务端识别：vendor/flarum/mentions/src/ConfigureMentions.php 中的五条常量正则
 *      （USER_MENTION_WITH_DISPLAY_NAME_REGEX / USER_MENTION_WITH_USERNAME_REGEX /
 *        POST_MENTION_WITH_DISPLAY_NAME_REGEX / GROUP_MENTION_WITH_NAME_REGEX /
 *        TAG_MENTION_WITH_SLUG_REGEX）。
 *   ② 前端产出：vendor/flarum/mentions/js/src/forum/mentionables/
 *      · AtMentionFormat.format(name, char, id) → `@"名"#${char}${id}`（有 id 时）
 *      · HashMentionFormat.format(slug)         → `#${slug}`
 *      · PostMention.replacement  → format(名, 'p', id)
 *      · UserMention.replacement  → format(名, '', id) 或旧式 format(用户名)
 *      · GroupMention.replacement → format(名, 'g', id)
 *      · TagMention.replacement   → format(slug)
 *   本文件的 5 条正则与上述服务端常量**逐字符对齐**，保证「编辑器认得的写法」
 *   与「服务端认得的写法」完全一致，不会出现编辑器渲染成小标签、服务端却当纯文本的错位。
 *
 * 【红线：逐字节回滚】
 *   序列化时优先原样输出 tokenizer 记录下的源码（attrs.raw），保证落库内容与改造前
 *   逐字节一致 ⇒ 服务端正则继续命中，@提醒 / 通知链路不受影响（见 extend.php 第四节）。
 *
 * 本文件是纯字符串处理（**不引用 tiptap / markdown-it / flarum**），供三处接入点复用：
 *   · 腿 B tokenizer ：把一段提及源码切成一个 mention 内联 token；
 *   · 腿 B getAttrs  ：把 token.content 还原成 Node 属性；
 *   · 腿 C 序列化器  ：把 Node 属性**原样**还原成提及源码。
 */

/** markdown-it token 类型名（tokenizer 产出、buildTokens 消费，两侧必须完全一致）。 */
export const MENTION_TOKEN = 'jiedh_mention';

/**
 * ProseMirror / Tiptap 节点名。
 * 必须与 buildTokens 里的 `node:` 值、buildNodes 里序列化器的 key、Tiptap Node 的 name
 * 三处一致（与 packages/mention/extend.php 头部注释里的「节点名 mention」保持一致）。
 */
export const MENTION_NODE = 'mention';

/** 提及种类。 */

/** 自研 Tiptap Node 的属性集合（Node 属性名 → 语义）。 */

/* -------------------------------------------------------------------------- */
/* 字符码常量（与 vendor/fof/rich-text 的 markdown-it 插件保持同一写法，便于对照） */
/* -------------------------------------------------------------------------- */
const CODE_AT = 0x40; // @
const CODE_HASH = 0x23; // #
const CODE_QUOTE = 0x22; // "  （直引号）
const CODE_QUOTE_LEFT = 0x201c; // “ （左全角引号，旧版 Flarum 产出过这种写法）
const CODE_LOWER_A = 0x61;
const CODE_LOWER_Z = 0x7a;
const CODE_UPPER_A = 0x41;
const CODE_UPPER_Z = 0x5a;
const CODE_DIGIT_0 = 0x30;
const CODE_DIGIT_9 = 0x39;
const CODE_UNDERSCORE = 0x5f;

/* -------------------------------------------------------------------------- */
/* 与服务端逐字符对齐的正则                                                     */
/* -------------------------------------------------------------------------- */
/*
 * ① safe 格式提及（帖子 / 用户 / 群组统一用一条）：
 *      @ "显示名" # [前缀字母] 数字 ID
 *    · 显示名部分 `((?:(?!"#[a-z]{0,3}[0-9]+).)+)` 与 ConfigureMentions 的
 *      `(?<displayname>((?!"#[a-z]{0,3}[0-9]+).)+)` **逐字符同构**：
 *      负向先行断言让贪婪匹配在「收尾引号 + # + 最多 3 个字母 + 数字」之前停下，
 *      从而把收尾引号正确地留给后面的 `["”|]`。
 *      ⚠️ 必须保留**最外层那对括号**：服务端用的是具名捕获组把整段重复包住，
 *      而 JS 里 `((?!…).)+` 这种「捕获组自带 +」只会保留**最后一次迭代**
 *      （对 @"admin"#p2 会得到 "n"）⇒ 编辑器小标签只显示末字符。详见第九节变更记录。
 *    · 收尾引号比服务端宽松一点：服务端「用户/帖子」只允许 " 与 ”，「群组」额外允许 |；
 *      这里统一允许三者。**只会让「识别」更宽容，不会让「产出」变样** ——
 *      序列化走 attrs.raw 原样输出，不会凭空造出服务端不认的写法。
 *    · 前缀 `[a-z]{0,3}`：'' → 用户，'p' → 帖子，'g' → 群组。
 *    · 捕获组编号：1 = 显示名（整段），2 = 前缀字母，3 = 数字 ID。
 */
const SAFE_MENTION_RE = /@["“]((?:(?!"#[a-z]{0,3}[0-9]+).)+)["”|]#([a-z]{0,3})([0-9]+)\b/y;

/*
 * ③ 旧式用户提及：@用户名（用户名 = [a-z0-9_-]，大小写不敏感）
 *    `(?!#)` 与服务端一致：@abc#def 这类会被回溯成 @ab（与服务端同样的行为，保持口径一致）。
 */
const USER_LEGACY_MENTION_RE = /@([a-z0-9_-]+)(?!#)/iy;

/*
 * ⑤ 标签提及：#slug（slug 允许 - _ 以及任意语言的字母 / 数字 / 组合附加符号）
 *    服务端写的是 /(?:[^“"]|^)\B#(?<slug>…)\b/ui，其中：
 *      · `\B`          —— 前一字符不能是 ASCII 词字符；
 *      · `[^“"]`       —— 前一字符也不能是全角左引号或直引号（避免把 @"名"#p1 里的 #p1 当成标签）；
 *    这两条前置条件在 tokenizer 里用字符码手工判断（见 isNonWordBoundaryBefore / matchMentionAt），
 *    正则本身只负责 # 之后的 slug 部分。
 */
const TAG_MENTION_RE = /#([-_\p{L}\p{N}\p{M}]+)\b/uy;

/** 一次成功的提及匹配：属性 + 结束位置（不含）。 */

/* -------------------------------------------------------------------------- */
/* 小工具                                                                     */
/* -------------------------------------------------------------------------- */

/** ASCII 词字符判断（与 JS 正则 \w / \b 的判定口径一致，含 `u` 标志下也仍是 ASCII）。 */
function isWordChar(code) {
  return code >= CODE_DIGIT_0 && code <= CODE_DIGIT_9 || code >= CODE_UPPER_A && code <= CODE_UPPER_Z || code >= CODE_LOWER_A && code <= CODE_LOWER_Z || code === CODE_UNDERSCORE;
}

/**
 * 判断 index 前的位置是否为「非词边界」（等价于服务端正则里的 `\B`）。
 * `\B` 成立 ⇔ 前一字符不是 ASCII 词字符（字符串开头也成立）。
 *   · `foo@bar` → 前一个字符是词字符 ⇒ 不是 \B ⇒ 不认（避免把邮箱当成提及）；
 *   · `(@bar`   → 前一个字符是 `(` ⇒ 是 \B ⇒ 认。
 */
function isNonWordBoundaryBefore(src, index) {
  if (index <= 0) return true;
  return !isWordChar(src.charCodeAt(index - 1));
}

/**
 * 在 src 的 index 处执行粘性（sticky）正则。
 * 使用 `y` 标志而非 `^` 锚定，避免对长字符串反复切片；命中时 m.index 必等于 index。
 */
function execAt(re, src, index) {
  re.lastIndex = index;
  const match = re.exec(src);
  return match && match.index === index ? match : null;
}

/** 由 safe 格式的前缀字母推断提及种类。 */
function kindFromPrefix(prefix) {
  if (prefix === 'p') return 'post';
  if (prefix === 'g') return 'group';

  // 空前缀 = 用户提及；其它未知前缀也按用户提及兜底（服务端同样只认这三种）
  return 'user';
}

/* -------------------------------------------------------------------------- */
/* 词法：识别                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * 从 src 的 start 处尝试解析一段提及源码（**不做前置边界检查**，供 matchMentionAt 与
 * parseMentionSource 共用）。与 markdown-it 的 tokenizer 契约无关，纯函数。
 *
 * @returns 命中的属性与结束位置；未命中返回 null。
 */
function parseFrom(src, start, max) {
  const code = src.charCodeAt(start);
  if (code === CODE_AT) {
    // 先试 safe 格式（@"名"#p1 / @"名"#12 / @"名"#g3），再试旧式 @用户名
    const safe = execAt(SAFE_MENTION_RE, src, start);
    if (safe && start + safe[0].length <= max) {
      return {
        attrs: {
          kind: kindFromPrefix(safe[2] ?? ''),
          displayName: safe[1] ?? null,
          id: safe[3] ?? null,
          slug: null,
          raw: safe[0]
        },
        end: start + safe[0].length
      };
    }
    const legacy = execAt(USER_LEGACY_MENTION_RE, src, start);
    if (legacy && start + legacy[0].length <= max) {
      return {
        attrs: {
          kind: 'user-legacy',
          displayName: legacy[1] ?? null,
          id: null,
          slug: null,
          raw: legacy[0]
        },
        end: start + legacy[0].length
      };
    }
    return null;
  }
  if (code === CODE_HASH) {
    const tag = execAt(TAG_MENTION_RE, src, start);
    if (tag && start + tag[0].length <= max) {
      return {
        attrs: {
          kind: 'tag',
          displayName: null,
          id: null,
          slug: tag[1] ?? null,
          raw: tag[0]
        },
        end: start + tag[0].length
      };
    }
    return null;
  }
  return null;
}

/**
 * 从 src 的 start 处尝试匹配一段提及源码，并施加与**服务端正则一致的前置边界条件**。
 *
 * 前置条件（缺一不可，否则就会出现「编辑器渲染成小标签、服务端却是纯文本」的错位）：
 *   · @ ：前一个字符必须是非词字符（服务端 `\B@`）；
 *   · # ：前一个字符必须是非词字符（服务端 `\B#`），且不能是直引号 " 或全角左引号 “
 *          （服务端 `(?:[^“"]|^)`，用于排除 @"名"#p1 里的 #p1）。
 *
 * @param src   markdown-it inline state 的 src（整段行内文本）
 * @param start 当前位置（state.pos）
 * @param max   行内文本上限（state.posMax）
 */
export function matchMentionAt(src, start, max) {
  const code = src.charCodeAt(start);
  if (code !== CODE_AT && code !== CODE_HASH) return null;
  if (!isNonWordBoundaryBefore(src, start)) return null;
  if (code === CODE_HASH) {
    const previous = start > 0 ? src.charCodeAt(start - 1) : -1;
    if (previous === CODE_QUOTE || previous === CODE_QUOTE_LEFT) return null;
  }
  return parseFrom(src, start, max);
}

/**
 * 解析一段**独立的**提及源码（例如 token.content 里的原始切片）。
 * 因为切片本身就始于 @ / #，`\B` 前置条件恒成立，故这里无需再判边界。
 */
export function parseMentionSource(raw) {
  if (!raw) return null;
  const match = parseFrom(raw, 0, raw.length);
  return match ? match.attrs : null;
}

/* -------------------------------------------------------------------------- */
/* 序列化                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 把 Node 属性还原成服务端认得的提及源码。
 *
 * 【为什么优先返回 attrs.raw】
 *   用户的历史数据里可能存在全角引号 “ ” 等写法，逐字节回放才能保证「编辑一次老帖」
 *   不会把正文里的提及写法悄悄改掉（extend.php 第四节红线）。只有在 raw 缺失
 *   （例如将来新增的写入路径没有记录 raw）时，才按属性重新拼装。
 */
export function formatMentionSource(attrs) {
  if (attrs.raw) return attrs.raw;
  switch (attrs.kind) {
    case 'post':
      return `@"${attrs.displayName ?? ''}"#p${attrs.id ?? ''}`;
    case 'group':
      return `@"${attrs.displayName ?? ''}"#g${attrs.id ?? ''}`;
    case 'tag':
      return `#${attrs.slug ?? ''}`;
    case 'user-legacy':
      return `@${attrs.displayName ?? ''}`;
    default:
      return `@"${attrs.displayName ?? ''}"#${attrs.id ?? ''}`;
  }
}

/**
 * markdown-it 内联规则：在当前位置尝试匹配一整段提及源码。
 *
 * 契约（与 vendor/fof/rich-text/…/markdown-it/altText.ts 完全一致，必须遵守）：
 *   · 返回 true 时**必须**已推进 state.pos，否则 ParserInline 会抛
 *     「inline rule didn't increment state.pos」；
 *   · silent 模式（markdown-it 的 lookahead / skipToken）只推进位置、不产出 token。
 *
 * 注册位置见 registerMentions.ts：`inline.ruler.before('link', …)`。
 * 之所以能轮到本规则：altText.ts 把 @ (0x40) 与 # (0x23) 列为终止符，
 * 文本规则会在它们之前停下。
 */
export function tokenizeMention(state, silent) {
  const src = state.src;
  const start = state.pos;
  const max = state.posMax;
  const matched = matchMentionAt(src, start, max);
  if (!matched || matched.end <= start) return false;
  if (!silent) {
    // 产出 node 型 token（nesting = 0）；tag 传空串：该 token 只用于喂给 MarkdownParser，
    // 不参与 markdown-it 自身的 HTML 输出（tokenizerParams 里 html 也是关闭的）。
    const token = state.push(MENTION_TOKEN, '', 0);
    // 只存原始切片，属性由 buildTokens 的 getAttrs → parseMentionSource 还原（与 upload 包同构）
    token.content = matched.attrs.raw;
  }
  state.pos = matched.end;
  return true;
}